import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  Shield,
  Save,
  Edit2,
  Camera,
  Calendar,
  LogOut,
  CreditCard,
  Briefcase
} from 'lucide-react';
import axios from 'axios';
import { getAuthHeader } from '../../helpers/auth'; // Adjust path as needed

const CustomerProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [stats, setStats] = useState({ totalJobs: 0, activeJobs: 0 });

  // Form States
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // 1. Load User from LocalStorage
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        setUser(userData);
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          company_name: userData.company_name || '',
          address: userData.address || '',
          city: userData.city || '',
          state: userData.state || '',
          zip_code: userData.zip_code || '',
        });
      }
      fetchUserStats();
    } catch (err) {
      console.error('Error loading user:', err);
    }
  }, []);

  // 2. Fetch Stats (Optional - to show total jobs on profile)
  const fetchUserStats = async () => {
    try {
        const config = getAuthHeader();
        // Hitting history endpoint just to get counts
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-jobs-history/history`, { 
            ...config, 
            params: { dateRange: 'all' } 
        });
        const jobs = response.data.jobs || [];
        setStats({
            totalJobs: jobs.length,
            activeJobs: jobs.filter(j => ['started', 'in_progress'].includes(j.status)).length
        });
    } catch (error) {
        console.log("Stats fetch error (non-critical):", error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
        const config = getAuthHeader();
        // Replace with your actual Update Profile Endpoint
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/users/profile`, formData, config);
        
        // Update Local Storage
        const updatedUser = { ...user, ...formData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        setIsEditing(false);
        alert('Profile updated successfully!');
    } catch (error) {
        console.error('Update failed:', error);
        alert('Failed to update profile.');
    } finally {
        setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA', padding: '24px', paddingBottom: '60px' }}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .profile-card {
          animation: fadeIn 0.5s ease-out;
          transition: all 0.3s ease;
        }
        .profile-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.08) !important;
        }
        .form-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #E5E7EB;
          border-radius: 12px;
          font-size: 14px;
          transition: all 0.2s;
          background: #F9FAFB;
        }
        .form-input:focus {
          border-color: #cc2899;
          background: white;
          outline: none;
          box-shadow: 0 0 0 4px rgba(204, 40, 153, 0.1);
        }
        .form-input:disabled {
            background: #F3F4F6;
            color: #9CA3AF;
            cursor: not-allowed;
        }
        .section-title {
            font-size: 18px; 
            font-weight: 700; 
            color: var(--nd-surface-2); 
            margin-bottom: 20px; 
            display: flex; 
            align-items: center; 
            gap: 10px;
        }
      `}</style>

      {/* Header Banner */}
      <div style={{
        background: "linear-gradient(135deg,#6528a9, #cc2899)",
        borderRadius: '24px',
        padding: '40px',
        paddingBottom: '80px', // Extra padding for overlapping card
        marginBottom: '-60px', // Negative margin to create overlap
        boxShadow: '0 10px 30px rgba(101, 40, 169, 0.3)',
        position: 'relative',
        color: 'white'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
                <h1 style={{ fontSize: '32px', fontWeight: '800', margin: 0, marginBottom: '8px' }}>My Profile</h1>
                <p style={{ opacity: 0.9, fontSize: '16px' }}>Manage your account settings and preferences</p>
            </div>
            <div style={{ 
                background: 'var(--nd-w20)', 
                backdropFilter: 'blur(10px)', 
                padding: '8px 16px', 
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <Shield size={16} />
                Secure Account
            </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
        
        {/* Left Column: Identity Card */}
        <div style={{ gridColumn: 'span 1' }}>
            <div className="profile-card" style={{ 
                background: 'white', 
                borderRadius: '20px', 
                padding: '32px 24px', 
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
                position: 'relative'
            }}>
                <div style={{ 
                    width: '120px', 
                    height: '120px', 
                    borderRadius: '50%', 
                    background: 'white',
                    padding: '4px',
                    margin: '0 auto 20px',
                    position: 'relative',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                }}>
                    <div style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #6528a9, #cc2899)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '40px',
                        fontWeight: '800'
                    }}>
                        {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    {/* Camera Icon Badge */}
                    <div style={{
                        position: 'absolute',
                        bottom: '0',
                        right: '0',
                        background: 'var(--nd-surface-2)',
                        color: 'white',
                        padding: '8px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        border: '3px solid white'
                    }}>
                        <Camera size={16} />
                    </div>
                </div>

                <h2 style={{ fontSize: '24px', fontWeight: '800', color: 'var(--nd-surface-2)', margin: 0 }}>{user?.name}</h2>
                <p style={{ color: '#6B7280', margin: '4px 0 24px', fontWeight: '500' }}>{user?.company_name || 'Commercial Client'}</p>

                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '32px' }}>
                    <div style={{ background: '#F3F4F6', padding: '12px', borderRadius: '12px', flex: 1 }}>
                        <div style={{ fontSize: '20px', fontWeight: '800', color: '#6528a9' }}>{stats.totalJobs}</div>
                        <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Total Jobs</div>
                    </div>
                    <div style={{ background: '#ECFDF5', padding: '12px', borderRadius: '12px', flex: 1 }}>
                        <div style={{ fontSize: '20px', fontWeight: '800', color: '#10B981' }}>{stats.activeJobs}</div>
                        <div style={{ fontSize: '11px', fontWeight: '600', color: '#059669', textTransform: 'uppercase' }}>Active</div>
                    </div>
                </div>

                <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '24px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#4B5563' }}>
                        <Mail size={18} color="#6528a9" />
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>{user?.email}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#4B5563' }}>
                        <Phone size={18} color="#6528a9" />
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>{user?.phone || 'No phone added'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#4B5563' }}>
                        <MapPin size={18} color="#6528a9" />
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>{user?.city ? `${user.city}, ${user.state}` : 'Location not set'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#4B5563' }}>
                        <Calendar size={18} color="#6528a9" />
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>Joined {new Date().getFullYear()}</span>
                    </div>
                </div>
            </div>
        </div>

        {/* Right Column: Settings Forms */}
        <div style={{ gridColumn: 'span 2' }}>
            {/* General Information Card */}
            <div className="profile-card" style={{ background: 'white', borderRadius: '20px', padding: '32px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <div className="section-title" style={{ margin: 0 }}>
                        <User size={24} color="#6528a9" />
                        General Information
                    </div>
                    <button 
                        onClick={() => setIsEditing(!isEditing)}
                        style={{ 
                            background: isEditing ? '#F3F4F6' : 'transparent', 
                            color: isEditing ? 'var(--nd-w12)' : '#6528a9', 
                            border: isEditing ? 'none' : '2px solid #6528a9',
                            padding: '8px 16px', 
                            borderRadius: '10px', 
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s'
                        }}
                    >
                        {isEditing ? 'Cancel Edit' : <><Edit2 size={16} /> Edit Profile</>}
                    </button>
                </div>

                <form onSubmit={handleUpdateProfile}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--nd-w12)', marginBottom: '8px' }}>Full Name</label>
                            <input className="form-input" name="name" value={formData.name} onChange={handleInputChange} disabled={!isEditing} placeholder="John Doe" />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--nd-w12)', marginBottom: '8px' }}>Email Address</label>
                            <input className="form-input" name="email" value={formData.email} disabled style={{ opacity: 0.7 }} title="Cannot change email" />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--nd-w12)', marginBottom: '8px' }}>Phone Number</label>
                            <input className="form-input" name="phone" value={formData.phone} onChange={handleInputChange} disabled={!isEditing} placeholder="+1 (555) 000-0000" />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--nd-w12)', marginBottom: '8px' }}>Company Name</label>
                            <input className="form-input" name="company_name" value={formData.company_name} onChange={handleInputChange} disabled={!isEditing} placeholder="Company Ltd." />
                        </div>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--nd-w12)', marginBottom: '8px' }}>Street Address</label>
                        <input className="form-input" name="address" value={formData.address} onChange={handleInputChange} disabled={!isEditing} placeholder="123 Solar Street" />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--nd-w12)', marginBottom: '8px' }}>City</label>
                            <input className="form-input" name="city" value={formData.city} onChange={handleInputChange} disabled={!isEditing} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--nd-w12)', marginBottom: '8px' }}>State</label>
                            <input className="form-input" name="state" value={formData.state} onChange={handleInputChange} disabled={!isEditing} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--nd-w12)', marginBottom: '8px' }}>Zip Code</label>
                            <input className="form-input" name="zip_code" value={formData.zip_code} onChange={handleInputChange} disabled={!isEditing} />
                        </div>
                    </div>

                    {isEditing && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button type="submit" disabled={loading} style={{ 
                                background: 'linear-gradient(135deg, #6528a9, #cc2899)', 
                                color: 'white', 
                                border: 'none', 
                                padding: '12px 24px', 
                                borderRadius: '12px', 
                                fontWeight: '700', 
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 4px 12px rgba(101, 40, 169, 0.3)'
                            }}>
                                {loading ? 'Saving...' : <><Save size={18} /> Save Changes</>}
                            </button>
                        </div>
                    )}
                </form>
            </div>

            {/* Security / Password Card */}
            <div className="profile-card" style={{ background: 'white', borderRadius: '20px', padding: '32px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                <div className="section-title">
                    <Shield size={24} color="#6528a9" />
                    Security & Password
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '40px' }}>
                    <div style={{ background: '#F9FAFB', padding: '20px', borderRadius: '16px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: 'var(--nd-surface-2)' }}>Password Tips</h4>
                        <ul style={{ paddingLeft: '20px', margin: 0, color: '#6B7280', fontSize: '13px', lineHeight: '1.6' }}>
                            <li>Use at least 8 characters</li>
                            <li>Include one special character</li>
                            <li>Include numbers</li>
                            <li>Don't use personal info</li>
                        </ul>
                    </div>

                    <form>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--nd-w12)', marginBottom: '8px' }}>Current Password</label>
                            <input type="password" className="form-input" name="currentPassword" onChange={handlePasswordChange} placeholder="••••••••" />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--nd-w12)', marginBottom: '8px' }}>New Password</label>
                            <input type="password" className="form-input" name="newPassword" onChange={handlePasswordChange} placeholder="••••••••" />
                        </div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--nd-w12)', marginBottom: '8px' }}>Confirm New Password</label>
                            <input type="password" className="form-input" name="confirmPassword" onChange={handlePasswordChange} placeholder="••••••••" />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button type="button" style={{ 
                                background: 'white', 
                                color: '#6528a9', 
                                border: '2px solid #6528a9', 
                                padding: '10px 20px', 
                                borderRadius: '12px', 
                                fontWeight: '700', 
                                cursor: 'pointer' 
                            }}>
                                Update Password
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

      </div>
    </div>
  );
};

export default CustomerProfile;